import { ChangeDetectorRef, Component, OnChanges, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { VTabComponent } from "@app/directives/_layout/v-tabs/v-tab.component";
import { AsyncObservableComponent } from "@app/directives/_misc/async-observable/async-observable.component";
import { IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { UserType } from "@app/enum/UserType";
import { IUser } from "@app/interfaces/IUser";
import { ImportStrategyReference } from "@app/main/modules/foundation/foundation.enums";
import { ICsvImportCompletionResultDTO } from "@app/main/modules/foundation/foundation.interfaces";
import { CurrentUserService } from "@app/services/currentUser.service";
import { LoginService } from "@app/services/login.service.ajs";
import { IResellerUserGroupMapping, ResellerUserGroupMappingService } from "@app/services/resellerUserGroupMapping.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, forkJoin, from } from "rxjs";
import { map, take } from "rxjs/operators";
import { IReseller } from "../../_models/IReseller";
import { IUserGroup } from "../../_models/IUserGroup";
import { AccountService } from "../../_services/account.service";
import { EngineerService } from "../../_services/engineer.service";
import { UserGroupService } from "../../_services/user-group.service";
import { UserService } from "../../_services/user.service";
import './engineer-users-list.component.scss';

@UntilDestroy()
@Component({
    selector: 'engineer-users-list',
    template: require('./engineer-users-list.component.html')
})
export class EngineerUsersComponent implements OnInit, OnChanges {
    engineers: IUser[];
    selectedEngineer: IUser;
    addNewUserGroup: boolean;
    userGroups: IUserGroup[];
    newUserGroup: IUserGroup;
    mappedResellerIds: number[];

    tableColumns: IVTableColumn<IUser>[];
    tableRows: IVTableRow[];

    filterTabs: IFilterBarTab<IUser>[] = [];

    hasReseller: boolean;
    selectedResellerId: number;
    resellers: IReseller[];
    resellerUserGroupMappings: IResellerUserGroupMapping[];

    saving: boolean;

    loading: boolean;
    savingUserGroup: boolean;

    dialog: VDialogRef;

    showCsvImporter: boolean;
    importStrategyRef: ImportStrategyReference = ImportStrategyReference.Engineer;

    currentUser: IUser;
    
    selectedTab: VTabComponent;

    @ViewChild('resetPasswordTemplate') resetPasswordTemplate: TemplateRef<any>;
    constructor(
        private cdr: ChangeDetectorRef,
        private readonly dialogService: DialogService,
        private readonly filterBarService: FilterBarService,
        private readonly loginService: LoginService,
        private readonly engineerService: EngineerService,
        private readonly translationService: TranslationService,
        private readonly accountService: AccountService,
        private readonly userService: UserService,
        private readonly userGroupService: UserGroupService,
        private readonly vTableService: VTableService,
        private readonly currentUserService: CurrentUserService,
        private readonly resellerUserGroupService: ResellerUserGroupMappingService) { }

    ngOnInit(): void {
        this.getData();
    }

    ngOnChanges(): void {
        this.createTable();
    }

    onRowClicked(row: IVTableRow): void {
        this.selectedEngineer = row.data;
    }
    
    resetPassword(): void {
        this.dialog = this.dialogService.open(this.resetPasswordTemplate, null);
    }

    confirmResetPassword(): void {
        this.loginService.sendForgotPasswordEmail(this.selectedEngineer.Email).then(() => {
            this.closeDialog();
        });
    }

    closeDialog(): void {
        this.dialog.close();
    }

    closeEdit(): void {
        this.selectedEngineer = null;
    }

    closeUserGroupSidebar(): void {
        this.addNewUserGroup = false;
    }

    addEngineer(): void {
        this.selectedEngineer = {
            Id: 0,
            Type: UserType.Engineer,
            IsActive: true
        };
    }

    addUserGroup(): void {
        this.addNewUserGroup = true;
        this.newUserGroup = {
            Id: 0,
            Description: "",
            Email: "",
            Phone: "",
            mappedResellerIds: []
        }
    }

    isUserGroupFormValid(): boolean {
        return !!this.newUserGroup?.Description?.length && (!this.hasReseller || (this.hasReseller && !!this.selectedResellerId));
    }

    saveUserGroup(): void {
        this.savingUserGroup = true;
        this.userGroupService.saveUserGroup(this.newUserGroup).pipe(untilDestroyed(this)).subscribe(() => {
            this.closeUserGroupSidebar();
            this.savingUserGroup = false;
        });
    }

    deactivateEngineer(deactivate: boolean): void {
        if (!deactivate || !this.selectedEngineer.IsActive) {
            return;
        }

        this.toggleStatus();
    }

    activateEngineer(): void {
        if (this.selectedEngineer.IsActive) {
            return;
        }

        this.toggleStatus();
    }

    onEngineerSaved(engineer: IUser): void {
        this.selectedEngineer = null;
        this.engineers = [
            ...this.engineers,
            engineer
        ];
    }
    
    openCsvImporter(): void {
        this.showCsvImporter = true;
    }

    closeCsvImporter(): void {
        this.showCsvImporter = false;
    }

    onCsvModelsSaved(event: ICsvImportCompletionResultDTO): void {
        const eventEngineers = JSON.parse(event.CreatedModels) as IUser[];

        this.engineerService.addCsvImportedEngineersToCache(eventEngineers);
    }

    onSelectedTabChanged($event: VTabComponent): void {
        this.selectedTab = $event
    }

    companyUpdated($event: IUserGroup): void {
        this.createResellerPills($event);

        const index = this.userGroups.findIndex(group => group.Id === $event.Id);

        if (index !== -1) {
            this.userGroups[index] = $event;
        } else {
            this.userGroups.push($event);
        }
    }

    //--

    private getData(): void {
        this.loading = true;

		combineLatest([
            this.engineerService.getEngineersWithMappings(),
            this.accountService.getResellers(),
            this.userGroupService.getUserGroups(),
            this.resellerUserGroupService.getMappings({ IsActive: true }),
            from(this.currentUserService.getCurrentUser() as Promise<IUser>)
        ]).pipe(
            take(1)
        ).subscribe(([engineers, resellers, userGroups, mappings, currentUser]) => {
            this.resellers = resellers;
            this.currentUser = currentUser;
            this.hasReseller = currentUser.IsReseller;
            this.engineers = engineers;
            this.userGroups = userGroups;
            this.resellerUserGroupMappings = mappings;

            this.userGroups.forEach(company => {
                company.mappedResellerIds = this.resellerUserGroupMappings.filter(m => m.UserGroupId === company.Id && m.EndTime == null).map(m => m.ResellerId);

                this.createResellerPills(company);
            });

            this.createTableRows()
            this.loading = false;
        });
    }

    private createResellerPills(company: IUserGroup) {
        
        company.resellerPills = [];
        const resellerPillsExportData: string[] = [];

        let extraCount = 0;

        company.mappedResellerIds.forEach((resellerId, index) => {
            const reseller = this.resellers.find(r => r.Id === resellerId);

            resellerPillsExportData.push(reseller.Description);

            if (index < 2) {
                company.resellerPills.push({
                    label: reseller.Description,
                    hovering: false,
                    data: reseller,
                    tooltip: reseller?.Description
                });
            } else {
                extraCount++;
            }            
        });

        if (extraCount > 0) {
            company.resellerPills.push({
                label: `+${extraCount}`,
                hovering: true,
                tooltip: resellerPillsExportData.slice(2).join(", ")
            })
        }

        company.resellerPillsExportData = resellerPillsExportData.join(",");
    }

    private toggleStatus(): void {
        this.saving = true;
        this.userService.toggleInternalUserStatus(this.selectedEngineer).pipe(untilDestroyed(this)).subscribe(() => {
            this.closeEdit();
            this.saving = false;
        });
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.engineers, this.tableColumns);
        this.createTabs();
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Name' }),
                createCellFunction: (engineer) => this.vTableService.createTableCell(engineer.Description)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Email' }),
                createCellFunction: (engineer) => this.vTableService.createTableCell(engineer.Email)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Status' }),
                createCellFunction: (engineer) => {
                    const isActiveText = engineer.IsActive ? this.translationService.t("UserStatusEnum.Active") : this.translationService.t("UserStatusEnum.Inactive");
                    return this.vTableService.createTableCell(isActiveText);
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "EngineerUsers.ParentCompany", minTableWidth: 900 }),
                createCellFunction: engineer => {
                    return this.vTableService.createTableCell(engineer.UserGroupId, {
                        component: AsyncObservableComponent,
                        componentInputs: {
                            useLoading: true,
                            observable$: this.userGroupService.getUserGroups({ Id: engineer.UserGroupId }).pipe(map(group => {
                                return group[0].Description;
                            }))
                        }
                    })
                }
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: 'users.LastLogin' }),
                createCellFunction: (engineer) => this.vTableService.createDateCell(engineer.lastLoggedIn)

            },
            

        ]
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IUser>("Common.All", (engineer) => true));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IUser>("UserStatusEnum.Active", (engineer) => engineer.IsActive));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IUser>("UserStatusEnum.Inactive", (engineer) => !engineer.IsActive));
    }
}
